import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { getMaxValue, getMinValue, ResourceId } from '@care-portals/shared';
import * as CustomerActions from './customer.actions';
import { CustomerEntity } from './customer.models';
import * as fromAppointment from '../appointment/appointment.reducer';
import { ICustomerCreditType, ILead } from '@appgen-health-crm/api-interfaces';

export const CUSTOMER_FEATURE_KEY = 'customer';

export interface State extends EntityState<CustomerEntity> {
  selectedId?: string; // which Customer record has been selected
  loading: boolean; //  if the customers list is loading
  saving: boolean; // if customer is saving
  error?: string | null; // last none error (if any)
  minId: string;
  maxId: string;
  selectedCustomerResources: {
    appointments: fromAppointment.State;
    lead: ILead;
  };
  createDrawer: {
    open: boolean;
    loading: boolean;
  };
  paymentMethodLoading: boolean;
}

export interface CustomerPartialState {
  readonly [CUSTOMER_FEATURE_KEY]: State;
}

export function selectCustomerId(customer: CustomerEntity): string {
  //In this case this would be optional since primary key is id
  return customer._id;
}

export const customerAdapter: EntityAdapter<CustomerEntity> =
  createEntityAdapter<CustomerEntity>({
    selectId: selectCustomerId
  });

export const initialState: State = customerAdapter.getInitialState({
  // set initial required properties
  loading: false,
  saving: false,
  error: null,
  minId: null,
  maxId: null,
  selectedCustomerResources: {
    appointments: fromAppointment.initialState,
    lead: null
  },
  createDrawer: {
    open: false,
    loading: false
  },
  paymentMethodLoading: false
});

const customerReducer = createReducer(
  initialState,
  on(CustomerActions.resetCustomers, state =>
    customerAdapter.removeAll({
      ...state,
      error: null,
      minId: null,
      maxId: null
    })
  ),
  on(CustomerActions.loadCustomers, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(CustomerActions.loadCustomersSuccess, (state, { customers, loadType }) => {
    const ids = customers.map(customer => customer._id);
    const newMaxId = getMaxValue(ids);
    const newMinId = getMinValue(ids);
    const maxId = newMaxId
      ? state.maxId > newMaxId
        ? state.maxId
        : newMaxId
      : state.maxId;
    const minId = newMinId
      ? state.minId < newMinId
        ? state.minId
        : newMinId
      : state.minId;

    const currentCustomers = Object.keys(state.entities).map(
      key => state.entities[key]
    );
    const newCustomers =
      loadType === 'newer'
        ? [...customers, ...currentCustomers]
        : [...currentCustomers, ...customers];

    return customerAdapter.setAll(newCustomers, {
      ...state,
      loading: false,
      maxId,
      minId
    });
  }),
  on(CustomerActions.loadCustomersFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  on(CustomerActions.loadCustomer, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(CustomerActions.loadCustomerSuccess, (state, { customer }) =>
    customerAdapter.upsertOne(customer, {
      ...state,
      loading: false,
      saving: false,
      selectedId: customer._id
    })
  ),
  on(CustomerActions.loadCustomerFailure, (state, { error }) => ({
    ...state,
    loading: false,
    selectedId: null,
    error
  })),

  on(CustomerActions.loadCustomerLead, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(CustomerActions.loadCustomerLeadSuccess, (state, { lead }) => ({
    ...state,
    loading: false,
    selectedCustomerResources: {
      ...state.selectedCustomerResources,
      lead
    }
  })),
  on(CustomerActions.loadCustomerLeadFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // customer activities
  on(CustomerActions.loadCustomerActivities, (state, { customerId }) =>
    customerAdapter.updateOne(
      {
        id: customerId,
        changes: {
          isActivitiesLoading: true
        }
      },
      {
        ...state
      }
    )
  ),
  on(
    CustomerActions.loadCustomerActivitiesSuccess,
    (state, { customerId, activities, hasMore }) =>
      customerAdapter.updateOne(
        {
          id: customerId,
          changes: {
            activities,
            isActivitiesLoading: false,
            hasMoreActivities: hasMore
          }
        },
        {
          ...state,
          loading: false,
          saving: false
        }
      )
  ),
  on(
    CustomerActions.loadCustomerActivitiesFailure,
    (state, { customerId, error }) =>
      customerAdapter.updateOne(
        {
          id: customerId,
          changes: {
            activities: [],
            isActivitiesLoading: false,
            activitiesError: error
          }
        },
        {
          ...state,
          loading: false,
          saving: false
        }
      )
  ),

  on(CustomerActions.createActivitySuccess, (state, { activity }) =>
    customerAdapter.updateOne(
      {
        id: ResourceId(activity.customer),
        changes: {
          activities: [
            activity,
            ...(state.entities[state.selectedId].activities || [])
          ]
        }
      },
      { ...state }
    )
  ),
  on(
    CustomerActions.openCustomerByBigcommerceIdSuccess,
    (state, { customer }) =>
      customerAdapter.upsertOne(customer, {
        ...state,
        selectedId: customer._id
      })
  ),
  on(CustomerActions.createCustomer, state => ({
    ...state,
    loading: true,
    createDrawer: {
      open: true,
      loading: true
    }
  })),
  on(CustomerActions.createCustomerSuccess, (state, { customer }) =>
    customerAdapter.addOne(customer, {
      ...state,
      loading: false,
      selectedId: customer._id,
      createDrawer: { open: false, loading: false }
    })
  ),
  on(CustomerActions.createCustomerFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
    createDrawer: { open: true, loading: false }
  })),
  on(CustomerActions.updateCustomer, state => ({
    ...state,
    saving: true,
    error: null
  })),
  on(CustomerActions.updateCustomerSuccess, (state, { customer }) =>
    customerAdapter.updateOne(customer, { ...state, saving: false })
  ),
  on(CustomerActions.updateCustomerFailure, (state, { error }) => ({
    ...state,
    error,
    saving: false
  })),

  on(CustomerActions.updateCustomerExtras, state => ({
    ...state,
    saving: true,
    error: null
  })),
  on(CustomerActions.updateCustomerExtrasSuccess, (state, { customer }) =>
    customerAdapter.updateOne(customer, { ...state, saving: false })
  ),
  on(CustomerActions.updateCustomerExtrasFailure, (state, { error }) => ({
    ...state,
    error,
    saving: false
  })),

  on(CustomerActions.updateWatchlist, state => ({
    ...state,
    saving: true,
    error: null
  })),
  on(CustomerActions.updateWatchlistFailure, (state, { error }) => ({
    ...state,
    error,
    saving: false
  })),
  on(CustomerActions.updateCustomer, state => ({
    ...state,
    saving: true,
    error: null
  })),
  on(CustomerActions.updateCustomerSuccess, (state, { customer }) =>
    customerAdapter.updateOne(customer, { ...state, saving: false })
  ),
  on(CustomerActions.loadPaymentMethods, (state, { id }) => ({
    ...state,
    paymentMethodLoading: true
  })),
  on(
    CustomerActions.loadPaymentMethodsSuccess,
    (state, { customerId, paymentMethods }) =>
      customerAdapter.updateOne(
        { id: customerId, changes: { paymentMethods } },
        { ...state, paymentMethodLoading: false }
      )
  ),
  on(CustomerActions.loadPaymentMethodsFailure, (state, { customerId }) =>
    customerAdapter.updateOne(
      { id: customerId, changes: { paymentMethods: [] } },
      { ...state, paymentMethodLoading: false }
    )
  ),
  /* ------------------------------------------------------------------------ */
  /*                       Customer Appointments Reducers                     */
  /* ------------------------------------------------------------------------ */

  on(CustomerActions.loadAppointments, state =>
    setCustomerResources(state, 'appointments', {
      loading: true,
      error: null
    })
  ),

  on(CustomerActions.loadAppointmentsSuccess, (state, { appointments }) =>
    setCustomerResources(state, 'appointments', {
      ...fromAppointment.appointmentAdapter.setAll(appointments, {
        ...state.selectedCustomerResources.appointments,
        loading: false
      })
    })
  ),

  on(CustomerActions.loadAppointmentsFailure, (state, { error }) =>
    setCustomerResources(state, 'appointments', {
      loading: false,
      error
    })
  ),

  on(CustomerActions.updateAppointment, state =>
    setCustomerResources(state, 'appointments', {
      updating: true,
      error: null
    })
  ),

  on(CustomerActions.updateAppointmentSuccess, (state, { appointment }) =>
    setCustomerResources(state, 'appointments', {
      ...fromAppointment.appointmentAdapter.upsertOne(appointment, {
        ...state.selectedCustomerResources.appointments,
        updating: false
      })
    })
  ),

  on(CustomerActions.updateAppointmentFailure, (state, { error }) =>
    setCustomerResources(state, 'appointments', {
      updating: false,
      error
    })
  ),

  on(
    CustomerActions.updateCustomerCreditSuccess,
    (state, { customerId, credit }) =>
      customerAdapter.updateOne(
        {
          id: customerId,
          changes: {
            credit
          }
        },
        state
      )
  ),

  on(CustomerActions.updateCustomerCreditFailure, (state, { error }) => ({
    ...state,
    error
  }))
);

function setCustomerResources(state: State, featureKey: string, updates: any) {
  return {
    ...state,
    selectedCustomerResources: {
      ...state.selectedCustomerResources,
      [featureKey]: {
        ...state.selectedCustomerResources[featureKey],
        ...updates
      }
    }
  };
}

export function reducer(state: State | undefined, action: Action) {
  return customerReducer(state, action);
}
